import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Slide } from "@shared/types";
import { QRCodeSVG } from "qrcode.react";
import { jumpGroup } from "@/lib/slide-factory";
import { mediaUrl, SharedGroupRef } from "@/lib/api";
import { formatTime } from "@/lib/clock";
import { Button } from "@/components/ui/button";

interface SlideExpandViewerProps {
  slides: Slide[];
  startIndex: number;
  sharedGroups?: Record<string, SharedGroupRef>;
  onClose: () => void;
}

/** Full-screen viewer for a single slide with keyboard navigation:
 *  ← / → move between slides, ↑ / ↓ jump between groups, Esc closes. */
export function SlideExpandViewer({ slides, startIndex, sharedGroups, onClose }: SlideExpandViewerProps) {
  const [index, setIndex] = useState(startIndex);

  const clamp = useCallback((i: number) => Math.max(0, Math.min(slides.length - 1, i)), [slides.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowRight":
          e.preventDefault();
          setIndex((i) => clamp(i + 1));
          break;
        case "ArrowLeft":
          e.preventDefault();
          setIndex((i) => clamp(i - 1));
          break;
        case "ArrowDown":
          e.preventDefault();
          setIndex((i) => jumpGroup(slides, i, 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setIndex((i) => jumpGroup(slides, i, -1));
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides, clamp, onClose]);

  const slide = slides[index];
  if (!slide) return null;

  let caption: string;
  if (slide.type === "groupref")
    caption = `Shared group: ${sharedGroups?.[slide.sharedGroupId]?.name ?? slide.name ?? "group"}`;
  else if (slide.type === "text") caption = slide.title || "Text slide";
  else if (slide.type === "clock") caption = "Clock / weather";
  else if (slide.type === "youtube") caption = slide.title || "YouTube video";
  else if (slide.type === "vimeo") caption = slide.title || "Vimeo video";
  else if (slide.type === "review") caption = `Review · ${slide.author || "patient"}`;
  else if (slide.type === "qr") caption = slide.title || "QR code";
  else if (slide.type === "team") caption = slide.name || "Team member";
  else caption = slide.fileName || slide.type;

  const groupLabel = slide.type !== "groupref" && slide.group ? ` · group “${slide.group}”` : "";

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95">
      <div className="flex items-center justify-between p-3 text-white">
        <div className="truncate text-sm">
          {index + 1} / {slides.length} · {caption}
          {groupLabel}
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-white/50 sm:inline">
            ← → slides · ↑ ↓ groups · Esc to close
          </span>
          <Button variant="secondary" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <button
          type="button"
          aria-label="Previous"
          onClick={() => setIndex((i) => clamp(i - 1))}
          className="absolute left-2 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        >
          <ChevronLeft className="h-7 w-7" />
        </button>

        <SlideBig slide={slide} sharedGroups={sharedGroups} />

        <button
          type="button"
          aria-label="Next"
          onClick={() => setIndex((i) => clamp(i + 1))}
          className="absolute right-2 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        >
          <ChevronRight className="h-7 w-7" />
        </button>
      </div>
    </div>
  );
}

function SlideBig({
  slide,
  sharedGroups,
}: {
  slide: Slide;
  sharedGroups?: Record<string, SharedGroupRef>;
}) {
  if (slide.type === "image") {
    return <img src={mediaUrl(slide.mediaKey)} alt="" className="max-h-full max-w-full object-contain" />;
  }
  if (slide.type === "video") {
    return <video src={mediaUrl(slide.mediaKey)} className="max-h-full max-w-full" controls />;
  }
  if (slide.type === "text") {
    return (
      <div
        className="flex h-full w-full items-center justify-center p-[6vw] text-center"
        style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
      >
        <div>
          {slide.title && <h1 className="mb-4 text-[5vw] font-bold">{slide.title}</h1>}
          {slide.body && <p className="whitespace-pre-wrap text-[2.5vw] opacity-90">{slide.body}</p>}
        </div>
      </div>
    );
  }
  if (slide.type === "clock") {
    return (
      <div
        className="flex h-full w-full items-center justify-center"
        style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
      >
        <div className="text-[10vw] font-bold tabular-nums">
          {formatTime(new Date(), slide.hour24, slide.showSeconds)}
        </div>
      </div>
    );
  }
  if (slide.type === "review") {
    const rating = Math.min(5, Math.max(1, slide.rating));
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center px-[10vw] text-center"
        style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
      >
        <div className="mb-[3vh] text-[5vw]" style={{ color: "#fbbf24" }}>
          {"★".repeat(rating)}
        </div>
        {slide.quote && <p className="max-w-[80%] text-[3.2vw] font-medium leading-snug">“{slide.quote}”</p>}
        <div className="mt-[4vh] text-[2vw] opacity-80">
          {slide.author && <span>— {slide.author}</span>}
          {slide.source !== "other" && <span> · on {slide.source === "google" ? "Google" : "Yelp"}</span>}
        </div>
      </div>
    );
  }
  if (slide.type === "youtube") {
    return slide.videoId ? (
      <img
        src={`https://i.ytimg.com/vi/${slide.videoId}/hqdefault.jpg`}
        alt=""
        className="max-h-full max-w-full object-contain"
      />
    ) : (
      <div className="text-white/60">YouTube (no video set)</div>
    );
  }
  if (slide.type === "vimeo") {
    return (
      <div className="text-center text-white/70">
        <p className="text-2xl font-semibold">Vimeo video</p>
        <p className="mt-2 text-white/50">{slide.videoId ? `id ${slide.videoId}` : "no video set"}</p>
      </div>
    );
  }
  if (slide.type === "team") {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-6 px-[6vw] text-center"
        style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
      >
        {slide.photoKey && (
          <img
            src={mediaUrl(slide.photoKey)}
            alt=""
            className="h-[36vh] w-[36vh] rounded-full object-cover"
          />
        )}
        {slide.name && <h1 className="text-[4vw] font-bold">{slide.name}</h1>}
        {slide.role && <p className="text-[2.4vw] opacity-80">{slide.role}</p>}
        {slide.bio && <p className="max-w-[70%] text-[1.8vw] opacity-90">{slide.bio}</p>}
      </div>
    );
  }
  if (slide.type === "qr") {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-6 px-[6vw] text-center"
        style={{ backgroundColor: slide.bgColor, color: slide.fgColor }}
      >
        {slide.title && <h1 className="text-[4vw] font-bold">{slide.title}</h1>}
        {slide.data ? (
          <div className="rounded-2xl bg-white p-4">
            <QRCodeSVG value={slide.data} size={1024} fgColor={slide.fgColor} style={{ width: "40vmin", height: "40vmin" }} />
          </div>
        ) : (
          <p className="opacity-60">No link set</p>
        )}
        {slide.caption && <p className="text-[2.2vw] opacity-90">{slide.caption}</p>}
      </div>
    );
  }
  // groupref
  const group = sharedGroups?.[slide.sharedGroupId];
  return (
    <div className="text-center text-white/70">
      <p className="text-2xl font-semibold">Shared group: {group?.name ?? slide.name ?? "group"}</p>
      <p className="mt-2 text-white/50">{group?.slides.length ?? 0} slides · edit it on its own page</p>
    </div>
  );
}
